import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2DataGrid,
  V2EmptyState,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  type V2BadgeTone,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  SalesInvoiceGlobalListResponse,
  SalesInvoiceListRow,
  SalesInvoiceStatus,
} from '../../lib/types/crm.js';
import { useApiGet } from '../../lib/useApiGet.js';

const PAGE_SIZE = 25;

const STATUS_LABEL: Record<SalesInvoiceStatus, string> = {
  draft: 'پیش‌نویس',
  submitted: 'ارسال‌شده برای تأیید',
  approved: 'تأییدشده',
  posted: 'ثبت قطعی',
  reversed: 'برگشت‌خورده',
};

const STATUS_TONE: Record<SalesInvoiceStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'warn',
  approved: 'info',
  posted: 'success',
  reversed: 'danger',
};

interface Filters {
  readonly search: string;
  readonly status: '' | SalesInvoiceStatus;
  readonly from: string;
  readonly to: string;
}

interface Row extends SalesInvoiceListRow {
  readonly id: string;
}

const EMPTY_FILTERS: Filters = { search: '', status: '', from: '', to: '' };

/** Read-only invoice index. Financial lifecycle actions remain in Accounting-controlled APIs. */
export function SalesInvoicesPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { unit } = useAmountDisplay();
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);

  const endpoint = useMemo(() => {
    if (!slug) return null;
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(PAGE_SIZE),
    });
    if (applied.search) params.set('search', applied.search);
    if (applied.status) params.set('status', applied.status);
    if (applied.from) params.set('from', applied.from);
    if (applied.to) params.set('to', applied.to);
    return `${base}/api/v1/sales/invoices?${params.toString()}`;
  }, [applied, base, offset, slug]);

  const invoices = useApiGet<SalesInvoiceGlobalListResponse>(endpoint, [endpoint]);

  if (!slug) return null;

  function applyFilters(event: FormEvent) {
    event.preventDefault();
    setOffset(0);
    setApplied({
      search: draft.search.trim(),
      status: draft.status,
      from: draft.from,
      to: draft.to,
    });
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setOffset(0);
  }

  const columns: readonly V2TableColumn<Row>[] = [
    {
      key: 'officialNumber',
      header: 'شماره فاکتور',
      render: (row) => (
        <Link to={`${base}/crm/invoices/${row.invoiceId}`} className="ltr">
          {row.officialNumber ?? 'در انتظار شماره قطعی'}
        </Link>
      ),
    },
    {
      key: 'partyName',
      header: 'مشتری',
      render: (row) => (
        <span>
          <PartyLink base={base} partyRef={row.partyRef} inRow>
            {row.partyName}
          </PartyLink>
          <small className="ltr" style={{ display: 'block', color: 'var(--sk-fg-subtle)' }}>
            {row.partyRef}
          </small>
        </span>
      ),
    },
    {
      key: 'orderRef',
      header: 'سفارش',
      render: (row) => (
        <Link to={`${base}/crm/orders/${row.orderRef}`} className="ltr">
          {row.orderRef}
        </Link>
      ),
    },
    {
      key: 'businessDate',
      header: 'تاریخ فاکتور',
      render: (row) => displayJalaliDate(row.businessDate),
    },
    {
      key: 'dueDate',
      header: 'سررسید',
      render: (row) => displayJalaliDate(row.dueDate),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => <V2Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</V2Badge>,
    },
    {
      key: 'totalAmountRial',
      header: amountColumnHeader('مبلغ فاکتور', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.totalAmountRial, unit)}</span>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <V2Card>
        <form onSubmit={applyFilters} style={{ padding: 16 }}>
          <V2FormGrid>
            <V2FormField label="جست‌وجو" width="wide">
              <input
                className="sk-input"
                value={draft.search}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, search: event.target.value }))
                }
                placeholder="شماره فاکتور، سفارش یا نام مشتری"
              />
            </V2FormField>
            <V2FormField label="وضعیت" width="medium">
              <select
                className="sk-select"
                value={draft.status}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    status: event.target.value as Filters['status'],
                  }))
                }
              >
                <option value="">همه وضعیت‌ها</option>
                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="از تاریخ" width="medium">
              <JalaliDateInput
                value={draft.from}
                onChange={(from) => setDraft((current) => ({ ...current, from }))}
              />
            </V2FormField>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput
                value={draft.to}
                onChange={(to) => setDraft((current) => ({ ...current, to }))}
              />
            </V2FormField>
          </V2FormGrid>
          <V2FormActions>
            <V2Button type="button" variant="secondary" onClick={clearFilters}>
              پاک‌کردن فیلترها
            </V2Button>
            <V2Button type="submit" variant="primary">
              اعمال فیلتر
            </V2Button>
          </V2FormActions>
        </form>
      </V2Card>

      {invoices.status === 'loading' ? <LoadingState /> : null}
      {invoices.status === 'error' ? (
        <StatePanel
          variant={invoices.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            invoices.error.status === 403
              ? 'دسترسی مشاهده فاکتورهای فروش را ندارید'
              : 'فهرست فاکتورهای فروش قابل نمایش نیست'
          }
        />
      ) : null}

      {invoices.status === 'ready' ? (
        <>
          <V2DataGrid
            icon="cart"
            title="فاکتورهای فروش"
            subtitle={`${invoices.data.total.toLocaleString('fa-IR')} فاکتور`}
            columns={columns}
            rows={invoices.data.invoices.map((invoice) => ({ ...invoice, id: invoice.invoiceId }))}
            rowKey={(invoice) => invoice.id}
            zebra
            emptyState={
              <V2EmptyState
                icon="cart"
                title="فاکتوری با این فیلترها پیدا نشد"
                description="فقط فاکتورهای در محدوده دسترسی شما نمایش داده می‌شوند."
                compact
              />
            }
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <V2Button
              type="button"
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
            >
              صفحه قبل
            </V2Button>
            <span style={{ color: 'var(--sk-fg-muted)', fontSize: 13 }}>
              {invoices.data.total === 0
                ? 'بدون نتیجه'
                : `${(offset + 1).toLocaleString('fa-IR')} تا ${Math.min(
                    offset + invoices.data.invoices.length,
                    invoices.data.total,
                  ).toLocaleString('fa-IR')}`}
            </span>
            <V2Button
              type="button"
              variant="secondary"
              disabled={offset + invoices.data.invoices.length >= invoices.data.total}
              onClick={() => setOffset((current) => current + PAGE_SIZE)}
            >
              صفحه بعد
            </V2Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
